/**
 * Error monitoring via Sentry.
 *
 * Fully driven by `VITE_SENTRY_DSN` (inlined at build time): when it is unset,
 * `initSentry` does nothing and every Sentry call stays a no-op, so local dev
 * and CI builds never report anywhere. Started by `src/instrument.ts`.
 *
 * Errors only — no tracing and no session replay, and `sendDefaultPii` is off:
 * client data never leaves its workspace (see docs/product.md). URLs can still
 * carry it — login navigates to `/verify-email?email=<address>` — so
 * every URL on an event or breadcrumb loses its query string and fragment, and
 * console breadcrumbs, which echo whatever the app logged, are dropped.
 */

import * as Sentry from "@sentry/react";

import { logger } from "@/lib/logger";

/** Breadcrumb `data` keys holding a URL: navigation `from`/`to`, fetch/xhr `url`. */
const BREADCRUMB_URL_KEYS = ["from", "to", "url"];

/** Initialise Sentry once, before the first render. Returns whether it is on. */
export function initSentry(): boolean {
  const dsn = import.meta.env.VITE_SENTRY_DSN?.trim();
  if (!dsn) {
    logger.debug("[sentry]", "VITE_SENTRY_DSN not set; error monitoring disabled");
    return false;
  }

  // Trimmed and lower-cased like the backend's ENVIRONMENT, so both projects
  // tag events with the same environment names.
  const environment = (
    import.meta.env.VITE_SENTRY_ENVIRONMENT?.trim() || import.meta.env.MODE
  ).toLowerCase();
  Sentry.init({
    dsn,
    environment,
    sendDefaultPii: false,
    beforeSend: scrubEvent,
    beforeBreadcrumb: scrubBreadcrumb,
  });
  logger.debug("[sentry]", "error monitoring enabled", { environment });
  return true;
}

/** Same shape the backend redacts: anything that looks like an email address. */
const EMAIL_PATTERN = /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g;
const EMAIL_REPLACEMENT = "[email]";
/** A URL or path inside free text followed by a query/fragment; `$1` keeps the path. */
const URL_WITH_QUERY_IN_TEXT = /((?:https?:\/\/|\/)[^\s?#]*)[?#]\S*/g;

/**
 * Keep client data out of an event: strip the query string and fragment from
 * its page URL and Referer, redact email addresses from exception messages and
 * the event message, and send only log templates (never their interpolated
 * values).
 */
function scrubEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  for (const exception of event.exception?.values ?? []) {
    if (typeof exception.value === "string") {
      exception.value = exception.value.replace(EMAIL_PATTERN, EMAIL_REPLACEMENT);
    }
  }
  if (typeof event.message === "string") {
    event.message = event.message.replace(EMAIL_PATTERN, EMAIL_REPLACEMENT);
  }
  if (event.logentry) {
    // `formatted` is the already-interpolated string; keep only the template.
    delete event.logentry.params;
    delete (event.logentry as { formatted?: string }).formatted;
    if (typeof event.logentry.message === "string") {
      event.logentry.message = event.logentry.message.replace(EMAIL_PATTERN, EMAIL_REPLACEMENT);
    }
  }
  const request = event.request;
  if (request?.url) {
    request.url = withoutQuery(request.url);
  }
  const headers = request?.headers;
  if (headers) {
    for (const [name, value] of Object.entries(headers)) {
      if (name.toLowerCase() === "referer") {
        headers[name] = withoutQuery(value);
      }
    }
  }
  return event;
}

/** Drop console breadcrumbs; strip the query string and fragment from URL ones. */
function scrubBreadcrumb(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb | null {
  if (breadcrumb.category === "console") {
    return null;
  }
  const data = breadcrumb.data;
  if (data) {
    for (const key of BREADCRUMB_URL_KEYS) {
      if (typeof data[key] === "string") {
        data[key] = withoutQuery(data[key]);
      }
    }
  }
  // Some breadcrumb shapes put the URL in the free-text message instead.
  if (typeof breadcrumb.message === "string") {
    breadcrumb.message = breadcrumb.message
      .replace(URL_WITH_QUERY_IN_TEXT, "$1")
      .replace(EMAIL_PATTERN, EMAIL_REPLACEMENT);
  }
  return breadcrumb;
}

/** Return `url` without its query string and fragment. */
function withoutQuery(url: string): string {
  return url.replace(/[?#].*/s, "");
}
